import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface BatchUploadProgress {
  status: 'idle' | 'creating_batch' | 'uploading' | 'completed' | 'partial_failure' | 'error';
  uploaded: number;
  total: number;
  failed: number;
  currentFile: string | null;
  failedFiles: Array<{ file: File; error: string }>;
  batchId: number | null;
  error: string | null;
}

interface UploadOptions {
  jobId?: string | null;
  opportunityId?: string | null;
  checklistStepRunId?: number;
  files: File[];
  description?: string;
  tagIds?: number[];
}

interface UsePhotoBatchUploadResult {
  progress: BatchUploadProgress;
  uploadPhotos: (options: UploadOptions) => Promise<{ uploaded: number; failed: number }>;
  retryFailed: () => Promise<{ uploaded: number; failed: number }>;
  reset: () => void;
  isUploading: boolean;
}

const initialProgress: BatchUploadProgress = {
  status: 'idle',
  uploaded: 0,
  total: 0,
  failed: 0,
  currentFile: null,
  failedFiles: [],
  batchId: null,
  error: null,
};

/**
 * Hook for uploading photos via presigned S3 URLs with progress tracking.
 * Files go directly from the browser to S3 — zero memory pressure on the web dyno.
 * Falls back to batch upload if presigned URL flow fails.
 */
export const usePhotoBatchUpload = (): UsePhotoBatchUploadResult => {
  const [progress, setProgress] = useState<BatchUploadProgress>(initialProgress);
  const queryClient = useQueryClient();

  // Store files, description and tags for retry functionality
  const pendingFilesRef = useRef<File[]>([]);
  const descriptionRef = useRef<string | undefined>(undefined);
  const tagIdsRef = useRef<number[] | undefined>(undefined);
  const checklistStepRunIdRef = useRef<number | undefined>(undefined);
  const optionsRef = useRef<{ jobId?: string | null; opportunityId?: string | null }>({});

  const reset = useCallback(() => {
    setProgress(initialProgress);
    pendingFilesRef.current = [];
    descriptionRef.current = undefined;
    tagIdsRef.current = undefined;
    optionsRef.current = {};
  }, []);

  const uploadSingleViaPresignedUrl = async (
    file: File,
    options: {
      jobId?: string | null;
      opportunityId?: string | null;
      checklistStepRunId?: number;
      batchId?: number;
      description?: string;
      tagIds?: number[];
    }
  ): Promise<void> => {
    // Step 1: Get presigned URL (creates Photo record in pending state)
    const presignResponse = await apiClient.getPresignedUploadUrl({
      jobId: options.jobId ? parseInt(options.jobId) : null,
      opportunityId: options.opportunityId ? parseInt(options.opportunityId) : null,
      checklistStepRunId: options.checklistStepRunId,
      batchId: options.batchId,
      filename: file.name,
      contentType: file.type || 'image/jpeg',
      description: options.description,
    });

    const { photo_id, presigned_url } = presignResponse.data;

    // Step 2: Upload file directly to S3 (bypasses web dyno)
    await apiClient.uploadFileToPresignedUrl(presigned_url, file);

    // Step 3: Confirm upload — triggers server-side processing and applies tags
    await apiClient.confirmPhotoUpload(photo_id, options.tagIds);
  };

  const uploadPhotos = useCallback(async (options: UploadOptions): Promise<{ uploaded: number; failed: number }> => {
    const { jobId, opportunityId, checklistStepRunId, files, description, tagIds } = options;

    if (files.length === 0) {
      return { uploaded: 0, failed: 0 };
    }

    // Store for potential retry
    pendingFilesRef.current = [];
    descriptionRef.current = description;
    tagIdsRef.current = tagIds;
    checklistStepRunIdRef.current = checklistStepRunId;
    optionsRef.current = { jobId, opportunityId };

    // Reset and start
    setProgress({
      ...initialProgress,
      status: 'creating_batch',
      total: files.length,
    });

    try {
      // Create batch for progress tracking
      const batchResponse = await apiClient.createPhotoBatch(
        jobId ? parseInt(jobId) : null,
        opportunityId ? parseInt(opportunityId) : null,
        files.length,
        description ? { description } : undefined
      );

      const batchId = batchResponse.data.id;

      setProgress(prev => ({
        ...prev,
        status: 'uploading',
        batchId,
      }));

      let uploaded = 0;
      let failed = 0;
      const failedFiles: Array<{ file: File; error: string }> = [];

      // Upload files one at a time via presigned URLs
      for (const file of files) {
        setProgress(prev => ({
          ...prev,
          currentFile: file.name,
        }));

        try {
          await uploadSingleViaPresignedUrl(file, {
            jobId,
            opportunityId,
            checklistStepRunId,
            batchId,
            description,
            tagIds,
          });
          uploaded++;

          setProgress(prev => ({
            ...prev,
            uploaded,
          }));
        } catch (error) {
          failed++;
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          failedFiles.push({ file, error: errorMessage });
          pendingFilesRef.current.push(file);

          setProgress(prev => ({
            ...prev,
            failed,
            failedFiles: [...prev.failedFiles, { file, error: errorMessage }],
          }));
        }
      }

      // Final status
      const finalStatus = failed === 0
        ? 'completed'
        : uploaded === 0
          ? 'error'
          : 'partial_failure';

      setProgress(prev => ({
        ...prev,
        status: finalStatus,
        currentFile: null,
      }));

      // Invalidate queries to refresh the UI
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ['jobPhotos', jobId] });
      }
      if (opportunityId) {
        queryClient.invalidateQueries({ queryKey: ['opportunityPhotos', opportunityId] });
      }

      return { uploaded, failed };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create batch';

      setProgress(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
        currentFile: null,
      }));

      // Store all files for retry
      pendingFilesRef.current = files;

      return { uploaded: 0, failed: files.length };
    }
  }, [queryClient]);

  const retryFailed = useCallback(async (): Promise<{ uploaded: number; failed: number }> => {
    const files = pendingFilesRef.current;
    const { jobId, opportunityId } = optionsRef.current;

    if (files.length === 0) {
      return { uploaded: 0, failed: 0 };
    }

    // Reset failed files before retry
    pendingFilesRef.current = [];

    setProgress(prev => ({
      ...prev,
      status: 'creating_batch',
      failed: 0,
      failedFiles: [],
      total: files.length,
      uploaded: 0,
      error: null,
    }));

    try {
      // Create a new batch for retry
      const batchResponse = await apiClient.createPhotoBatch(
        jobId ? parseInt(jobId) : null,
        opportunityId ? parseInt(opportunityId) : null,
        files.length,
        descriptionRef.current ? { description: descriptionRef.current } : undefined
      );

      const batchId = batchResponse.data.id;

      setProgress(prev => ({
        ...prev,
        status: 'uploading',
        batchId,
      }));

      let uploaded = 0;
      let failed = 0;
      const failedFiles: Array<{ file: File; error: string }> = [];

      for (const file of files) {
        setProgress(prev => ({
          ...prev,
          currentFile: file.name,
        }));

        try {
          await uploadSingleViaPresignedUrl(file, {
            jobId,
            opportunityId,
            checklistStepRunId: checklistStepRunIdRef.current,
            batchId,
            description: descriptionRef.current,
            tagIds: tagIdsRef.current,
          });
          uploaded++;

          setProgress(prev => ({
            ...prev,
            uploaded,
          }));
        } catch (error) {
          failed++;
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          failedFiles.push({ file, error: errorMessage });
          pendingFilesRef.current.push(file);

          setProgress(prev => ({
            ...prev,
            failed,
            failedFiles: [...prev.failedFiles, { file, error: errorMessage }],
          }));
        }
      }

      const finalStatus = failed === 0
        ? 'completed'
        : uploaded === 0
          ? 'error'
          : 'partial_failure';

      setProgress(prev => ({
        ...prev,
        status: finalStatus,
        currentFile: null,
      }));

      // Invalidate queries
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ['jobPhotos', jobId] });
      }
      if (opportunityId) {
        queryClient.invalidateQueries({ queryKey: ['opportunityPhotos', opportunityId] });
      }

      return { uploaded, failed };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create batch';

      // Put files back for another retry
      pendingFilesRef.current = files;

      setProgress(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
        currentFile: null,
      }));

      return { uploaded: 0, failed: files.length };
    }
  }, [queryClient]);

  const isUploading = progress.status === 'creating_batch' || progress.status === 'uploading';

  return {
    progress,
    uploadPhotos,
    retryFailed,
    reset,
    isUploading,
  };
};
